import type { routes } from '@semios/app-platform-banyan-route-definitions'
import type { Moment } from 'moment-timezone'
import moment from 'moment-timezone'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import type { Contact, Group } from '../../../ContactsAndGroups/ContactsAndGroupsContext'
import type { WeatherForecastAlertOnly } from '../../_types'

export type TWeatherForecastVariables = NonNullable<
  routes.LegacyWeatherForecastAlertsManagement.Request['legacyWeatherForecastAlertsToCreate']
>[number]

export const getAlertVariablesForWeatherForecastQuery = ({
  contacts,
  coverageBlocks,
  coverageProperties,
  coverageStations,
  coverageType,
  endDate,
  groups,
  hourOfDay,
  name,
  notifyOwnerByEmail,
  notifyOwnerBySMS,
  rules,
  startDate,
}: {
  contacts: Contact[]
  coverageBlocks: string[]
  coverageProperties: number[]
  coverageStations: string[]
  coverageType: string
  endDate: Moment | null
  groups: Group[]
  hourOfDay: string
  name: string
  notifyOwnerByEmail: boolean
  notifyOwnerBySMS: boolean
  rules: WeatherForecastAlertOnly['rules']
  startDate: Moment | null
}): TWeatherForecastVariables => {
  const { temperature, speed } = userDetailsStore.getState()

  const shapedContacts: TWeatherForecastVariables['contacts'] = contacts
    .filter(
      (
        c,
      ): c is Contact & {
        alertsContactMethods: NonNullable<TWeatherForecastVariables['contacts']>[number]['contactMethods']
      } => Array.isArray(c.alertsContactMethods) && c.alertsContactMethods.length > 0,
    )
    .map((c) => ({
      contactId: c.id,
      contactMethods: c.alertsContactMethods,
    }))

  const shapedGroups: TWeatherForecastVariables['groups'] = groups
    .filter(
      (
        g,
      ): g is Group & {
        alertsContactMethods: NonNullable<TWeatherForecastVariables['groups']>[number]['contactMethods']
      } => Array.isArray(g.alertsContactMethods) && g.alertsContactMethods.length > 0,
    )
    .map((g) => ({
      groupId: g.id,
      contactMethods: g.alertsContactMethods,
    }))

  let alertRules: TWeatherForecastVariables['alertRules']

  if ('windGreaterThanOrEqualToUserUnits' in rules) {
    alertRules = {
      windGreaterThanOrEqualToKph: Number(
        unitConverter
          .windSpeed(rules.windGreaterThanOrEqualToUserUnits, {
            inputUnit: speed === 'METRIC' ? 'kph' : 'mph',
            outputUnit: 'kph',
          })
          .value(),
      ),
    }
  } else if ('includeAllDevices' in rules && 'measurementHeights' in rules) {
    if ('temperatureLessThanOrEqualToUserUnits' in rules) {
      alertRules = {
        includeAllDevices: rules.includeAllDevices,
        measurementHeights: rules.measurementHeights,
        temperatureLessThanOrEqualToCelsius: Number(
          unitConverter
            .temperatureInCanopy(rules.temperatureLessThanOrEqualToUserUnits, {
              inputUnit: temperature === 'METRIC' ? 'c' : 'f',
              outputUnit: 'c',
            })
            .value(),
        ),
      }
    } else {
      alertRules = {
        includeAllDevices: rules.includeAllDevices,
        measurementHeights: rules.measurementHeights,
        temperatureGreaterThanOrEqualToCelsius: Number(
          unitConverter
            .temperatureInCanopy(rules.temperatureGreaterThanOrEqualToUserUnits, {
              inputUnit: temperature === 'METRIC' ? 'c' : 'f',
              outputUnit: 'c',
            })
            .value(),
        ),
      }
    }
  } else {
    alertRules = { hasRain: true }
  }

  const variables: TWeatherForecastVariables = {
    blockIds: null,
    contacts: shapedContacts,
    groups: shapedGroups,
    endDate: null,
    name,
    notifyOwnerByEmail,
    notifyOwnerBySMS,
    propertyIds: null,
    startDate: null,
    stationGeoms: null,
    hourOfDay,
    alertRules,
  }

  if (startDate && endDate) {
    // startDate and endDate have to be MM-DD
    variables.startDate = moment.tz(startDate, 'America/Los_Angeles').startOf('day').format('MM-DD')

    variables.endDate = moment.tz(endDate, 'America/Los_Angeles').startOf('day').format('MM-DD')
  }

  if (coverageType === 'properties' && coverageProperties && coverageProperties.length) {
    variables.propertyIds = coverageProperties.map(Number)
  }

  if (
    (coverageType === 'stationsOrBlocks' && coverageStations && coverageStations.length) ||
    (coverageBlocks && coverageBlocks.length)
  ) {
    let propertiesAndStations = coverageStations.map((v) => v.split('&')) // will return something like [50617, 0101000020E6100000B98C9B1A68085EC05DA9674128754240]
    let propertiesAndBlocks = coverageBlocks.map((v) => v.split('&')) // will return something like [50930, 16672]

    variables.stationGeoms = [...new Set(propertiesAndStations.map(([, stationId]) => stationId))]

    variables.blockIds = [...new Set(propertiesAndBlocks.map(([, blockId]) => +blockId))]

    variables.propertyIds = [
      ...new Set(propertiesAndStations.concat(propertiesAndBlocks).map(([propertyId]) => +propertyId)),
    ]
  }

  return variables
}
